import { useEffect, useRef, useState } from 'react';

export default function AutomatedForms() {
  const [animated, setAnimated] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setAnimated(true);
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const steps = [
    {
      step: '01',
      label: 'Dictate or drop in your notes',
      detail: 'Talk through the matter or paste in what you have. A few sentences is enough.',
    },
    {
      step: '02',
      label: 'We identify the right form',
      detail: 'HushLegal reads the context and pulls the correct court form automatically.',
    },
    {
      step: '03',
      label: 'Download and file',
      detail: 'A completed, court-ready PDF. Review it, sign it, file it.',
    },
  ];

  return (
    <section ref={sectionRef} className="relative py-20 sm:py-32 bg-white">
      <div className="max-w-[1100px] mx-auto px-5 sm:px-8">

        <div className={`max-w-[700px] mb-12 sm:mb-16 transition-all duration-700 ${animated ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <p className="text-[#8B4513] text-xs font-semibold tracking-widest uppercase mb-4">
            Court forms
          </p>
          <h2 className="text-[#09090b] text-[28px] sm:text-[40px] lg:text-[52px] font-bold leading-tight mb-5">
            Stop filling out forms by hand
          </h2>
          <p className="text-[#52525b] text-base sm:text-lg font-light leading-relaxed">
            Michigan court forms are tedious. They have always been tedious. You dictate or supply your notes and HushLegal fills the form out for you. That is the whole feature.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12 mb-12 sm:mb-16">
          {steps.map((s, index) => (
            <div
              key={index}
              className="flex flex-col gap-4"
              style={{
                transitionDelay: `${index * 150}ms`,
                opacity: animated ? 1 : 0,
                transform: animated ? 'translateY(0)' : 'translateY(24px)',
                transition: 'opacity 0.7s ease, transform 0.7s ease',
              }}
            >
              <h3 className="text-[#09090b] text-base sm:text-lg font-semibold leading-snug">{s.label}</h3>
              <p className="text-[#71717a] text-[14px] sm:text-[15px] font-light leading-relaxed">{s.detail}</p>
            </div>
          ))}
        </div>

        <div
          className={`grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-[820px] transition-all duration-700 delay-500 ${animated ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="p-6 sm:p-8 bg-[#f5f0e8] border border-[#e8dcc8] rounded-2xl">
            <p className="text-[#8B4513] text-xs font-semibold tracking-widest uppercase mb-3">Starting with Michigan</p>
            <p className="text-[#09090b] text-base font-medium leading-relaxed mb-2">
              Full SCAO form support across all 83 Michigan counties.
            </p>
            <p className="text-[#71717a] text-sm font-light leading-relaxed">
              Motions, orders, summonses, judgments, and more. If a Michigan court requires it, HushLegal can fill it out.
            </p>
          </div>
          <div className="p-6 sm:p-8 bg-[#f5f0e8] border border-[#e8dcc8] rounded-2xl">
            <p className="text-[#8B4513] text-xs font-semibold tracking-widest uppercase mb-3">Built for any jurisdiction</p>
            <p className="text-[#09090b] text-base font-medium leading-relaxed mb-2">
              Any state. Any court system. Any form.
            </p>
            <p className="text-[#71717a] text-sm font-light leading-relaxed">
              Our form automation works across state jurisdictions. If your court has standardized forms, we can automate them.
            </p>
          </div>
        </div>

      </div>
    </section>
  );
}
